import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Key, 
  AlertTriangle, 
  Server, 
  FileCheck, 
  Eye, 
  ArrowLeft, 
  CheckCircle2, 
  Terminal, 
  Sliders, 
  Layers,
  Database,
  ShieldAlert,
  Cpu
} from 'lucide-react';
import { AppView } from '../../types';
import { MOCK_SECURITY_ASSETS, MOCK_VULNERABILITIES, MOCK_SECURITY_ALERTS } from '../../services/security/securityStore';

interface CisoCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const CisoCommandCenter: React.FC<CisoCommandCenterProps> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'vulns' | 'siem' | 'secrets' | 'compliance'>('overview');
  const [alerts, setAlerts] = useState(MOCK_SECURITY_ALERTS);

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#1A1A1A] pb-16">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('homepage')}
              className="p-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-xl text-[#1A1A1A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-emerald-100 text-[#0E7044] rounded font-mono text-[10px] font-bold">ZERO TRUST & SECURE BY DESIGN</span>
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#0E7044]">
                  YamooPay CISO Command Center (Prompt 17)
                </span>
              </div>
              <h1 className="text-2xl font-black tracking-tight text-[#1A1A1A]">
                Global Security, Privacy, Trust & Cyber Defense Platform
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onNavigateToView('admin-super-command-center')}
              className="px-4 py-2 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Super Admin Control Center</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="max-w-7xl mx-auto px-6 mt-6">
        <div className="flex border-b border-[#E5E7EB] gap-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Vue Exécutive de Sécurité', icon: ShieldCheck },
            { id: 'assets', label: `Inventaire des Actifs (${MOCK_SECURITY_ASSETS.length})`, icon: Server },
            { id: 'vulns', label: `Gestion des Vulnérabilités (${MOCK_VULNERABILITIES.length})`, icon: AlertTriangle },
            { id: 'siem', label: `SIEM & Événements (${alerts.length})`, icon: Terminal },
            { id: 'secrets', label: 'Gestion des Secrets & Chiffrement', icon: Key },
            { id: 'compliance', label: 'Conformité OWASP & ASVS', icon: FileCheck },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`pb-3 text-xs font-bold border-b-2 transition-colors flex items-center gap-2 shrink-0 cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-[#0E7044] text-[#0E7044]'
                    : 'border-transparent text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Contents */}
        <div className="mt-8">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Executive KPI Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">Risques Critiques Ouverts</div>
                  <div className="text-3xl font-black text-[#0E7044] mt-2">0</div>
                  <div className="mt-2 text-xs text-[#0E7044] font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Posture Zero Trust Active
                  </div>
                </div>

                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">MFA & Passkeys Admins</div>
                  <div className="text-3xl font-black text-[#1A1A1A] mt-2">100%</div>
                  <div className="mt-2 text-xs text-[#6B7280]">Protection anti-phishing renforcée</div>
                </div>

                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">Bases de Données Publiques</div>
                  <div className="text-3xl font-black text-[#0E7044] mt-2">0</div>
                  <div className="mt-2 text-xs text-[#6B7280]">PostgreSQL & Redis isolés en interne</div>
                </div>

                <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs">
                  <div className="text-xs font-bold text-[#6B7280] uppercase">Rotation des Secrets</div>
                  <div className="text-3xl font-black text-[#1A1A1A] mt-2">À jour</div>
                  <div className="mt-2 text-xs text-[#6B7280]">Aucun secret en clair dans le code</div>
                </div>
              </div>

              {/* Core Security Principles */}
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 shadow-2xs">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#0E7044]" />
                  <span>Piliers de Cyberdéfense YamooPay (Prompt 17)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-[#4B5563]">
                  <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                    <strong className="text-[#1A1A1A] block mb-1">Architecture Zero Trust</strong>
                    Jamais de confiance aveuglément accordée à une requête sous prétexte qu'elle provient du réseau interne ou d'un conteneur adjacent.
                  </div>
                  <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                    <strong className="text-[#1A1A1A] block mb-1">Défense en Profondeur API</strong>
                    Chaque endpoint valide rigoureusement l'autorisation au niveau de l'objet (BOLA/IDOR defense) et applique des limites de débit par coût.
                  </div>
                  <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                    <strong className="text-[#1A1A1A] block mb-1">Sécurité de la Chaîne Logistique</strong>
                    Génération automatique de SBOM pour chaque artefact de production et analyse continue des dépendances et conteneurs Docker.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'assets' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Inventaire des Actifs & Surface d'Attaque</h3>
              <div className="space-y-3">
                {MOCK_SECURITY_ASSETS.map((asset) => (
                  <div key={asset.id} className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-[#1A1A1A]">{asset.name}</span>
                        <span className="px-2 py-0.5 bg-gray-200 text-[#4B5563] rounded font-mono text-[10px]">{asset.type}</span>
                      </div>
                      <div className="text-xs text-[#6B7280] mt-1">
                        Environnement : <strong className="text-[#1A1A1A]">{asset.environment}</strong> | Critique : <strong className="text-[#1A1A1A]">{asset.criticality}</strong>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                        asset.internetExposed ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-[#0E7044]'
                      }`}>
                        {asset.internetExposed ? 'Exposé Internet' : 'Isolé Interne'}
                      </span>
                      <span className="px-3 py-1 bg-emerald-50 text-[#0E7044] border border-emerald-200 rounded-full text-xs font-bold">
                        {asset.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'vulns' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Gestion des Vulnérabilités & Priorisation</h3>
              <div className="space-y-3">
                {MOCK_VULNERABILITIES.map((v) => (
                  <div key={v.id} className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-amber-600">{v.id}</span>
                        <span className="font-bold text-sm text-[#1A1A1A]">{v.title}</span>
                        {v.cve && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-mono text-[10px]">{v.cve}</span>}
                      </div>
                      <div className="text-xs text-[#6B7280] mt-1">Actif concerné : {v.asset} | CVSS : {v.cvss}</div>
                    </div>
                    <div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                        {v.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'siem' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">SIEM & Télémétrie de Sécurité</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] text-[#6B7280] font-bold">
                      <th className="pb-3">Horodatage</th>
                      <th className="pb-3">Type d'Événement</th>
                      <th className="pb-3">Sévérité</th>
                      <th className="pb-3">Source</th>
                      <th className="pb-3 text-right">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {alerts.map((alt) => (
                      <tr key={alt.id} className="text-[#1A1A1A]">
                        <td className="py-3 font-mono text-[#6B7280]">{new Date(alt.timestamp).toLocaleTimeString()}</td>
                        <td className="py-3 font-mono font-bold">{alt.type}</td>
                        <td className="py-3">
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-bold">{alt.severity}</span>
                        </td>
                        <td className="py-3 font-mono text-[#6B7280]">{alt.source}</td>
                        <td className="py-3 text-right">
                          <span className="px-2 py-0.5 bg-emerald-100 text-[#0E7044] rounded text-[10px] font-bold">{alt.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'secrets' && (
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-2xs space-y-4">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Gestion des Secrets & Chiffrement au Repos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                  <div className="font-bold text-sm text-[#1A1A1A]">Stockage des Clés & Secrets</div>
                  <p className="text-xs text-[#6B7280] mt-1">Isolation stricte via les variables d'environnement Dokploy. Aucun secret dans les dépôts Git.</p>
                </div>
                <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                  <div className="font-bold text-sm text-[#1A1A1A]">Hachage des Mots de Passe</div>
                  <p className="text-xs text-[#6B7280] mt-1">Utilisation d'algorithmes robustes résistants aux attaques par force brute (Argon2id / bcrypt).</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="bg-white border border-[#F9FAFB] rounded-2xl p-6 shadow-2xs space-y-4 bg-white border border-[#E5E7EB]">
              <h3 className="text-lg font-bold text-[#1A1A1A]">Référentiels OWASP & ASVS</h3>
              <div className="space-y-3">
                {[
                  { title: 'OWASP Top 10 Application Security Risks', status: 'Conforme & Intégré', desc: 'Protections exhaustives contre les injections SQL, XSS, et failles d\'authentification.' },
                  { title: 'OWASP API Security Top 10', status: 'Conforme & Testé', desc: 'Vérification stricte de l\'autorisation au niveau de l\'objet (BOLA) sur tous les endpoints.' },
                  { title: 'OWASP LLM / GenAI Security', status: 'Intégré', desc: 'Défense contre l\'injection de prompts indirecte, l\'empoisonnement RAG et l\'abus de privilèges d\'outils.' },
                ].map((ref, idx) => (
                  <div key={idx} className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl flex items-center justify-between text-xs">
                    <div>
                      <strong className="text-[#1A1A1A] block mb-1">{ref.title}</strong>
                      <span className="text-[#6B7280]">{ref.desc}</span>
                    </div>
                    <span className="px-3 py-1 bg-emerald-100 text-[#0E7044] rounded-full font-bold">{ref.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
